import { TableFieldModel } from '@/model/TableList'
import { priceWithoutRate } from '@/utils/index'

interface PurchaseDetail extends Record<string | number, any> {
  id: number;
  type: number;
  mark: string;
  no: string;
  originId: number;
  purchaseId: number;
  containsRate: boolean;
  prePurchaseIds: string;
  supplierId: number;
  materialId: number;
  amount: number;
  storageNum: number;
  fee: number;
  rate: number;
  paid: number;
  total: number;
  createTime: string; // Assuming LocalDateTime is represented as a string
  createUser: number;
  updateTime: string; // Assuming LocalDateTime is represented as a string
  updateUser: number;
  status: number;
  virtualStorage: boolean;
  invoiceStatus: number;
  paymentStatus: number;
  storageStatus: number;
}

interface Material extends Record<string | number, any> {
  id: number;
  no: string;
  materialClassId: number;
  materialClass: string;
  materialUnitId: number;
  materialUnit: string;
  brand: string;
  name: string;
  model: string;
  code: string;
  parameter: string;
  standard: string;
  device: string;
  mark: string;
  createTime: string; // Assuming LocalDateTime is represented as a string
  createUser: number;
  updateTime: string; // Assuming LocalDateTime is represented as a string
  updateUser: number;
  status: number;
  maxSupplierId: number;
  minSupplierId: number;
  maxInquiry: string;
  minInquiry: string;
  images: string;
}

interface Supplier {
  id: number;
  name: string;
  mainBusiness: string;
  website: string;
  contactNumber: string;
  email: string;
  address: string;
  addressDetail: string;
  mark: string;
  titleName: string;
  taxId: string;
  registeredAddress: string;
  bank: string;
  account: string;
  phone: string;
  changeStatus: number;
  createTime: string; // Assuming LocalDateTime is represented as a string
  createUser: number;
  updateTime: string; // Assuming LocalDateTime is represented as a string
  updateUser: number;
  status: number;
  accounts: string;
}

interface AccountInfo extends Record<string | number, any> {
  type: number;
  name: string;
  no: string;
  bank: string;
  bankNo: string;
  alias: string;
  corporate: number;
}

interface PaymentResModel extends Record<string | number, any> {
  id: number;
  originId: number;
  account: string;
  amount: number; // Assuming BigDecimal is represented as a number
  date: string; // Assuming LocalDate is represented as a string
  paymentMark: string;
  createTime: string; // Assuming LocalDateTime is represented as a string
}

interface PurchaseDetailInvoiceResModel extends Record<string | number, any> {
  id: number;
  purchaseId: number;
  purchaseDetailId: number;
  invoiceType: number;
  rate: number; // Assuming BigDecimal is represented as a number
  invoiceNo: string;
  invoiceCode: string;
  invoiceCheckCode: string;
  invoiceAmount: number; // Assuming BigDecimal is represented as a number
  invoiceDate: string; // Assuming LocalDate is represented as a string
  createTime: string; // Assuming LocalDateTime is represented as a string
  createUser: number;
  updateTime: string; // Assuming LocalDateTime is represented as a string
  updateUser: number;
  status: number;
  invoiceTypeValue: string;
}

interface PurchaseDetailPageModel extends PurchaseDetail {
  material: Material;
  supplier: Supplier;
  account: AccountInfo;
  createUserName: string;
  invoiceType: string;
  approveUser: string;
  approveTime: string; // Assuming LocalDateTime is represented as a string
  invoiceStatusValue: string;
  paymentStatusValue: string;
  storageStatusValue: string;
  typeValue: string;
  requestUsers?: string[];
  payments?: PaymentResModel[];
  invoices?: PurchaseDetailInvoiceResModel[];
}

export const TableField: TableFieldModel<PurchaseDetailPageModel>[] = [
  {
    componentType: 'el-input',
    componentName: '输入框',
    fieldKey: 'no',
    fieldName: '采购编号'
  },
  {
    componentType: 'el-input',
    componentName: '输入框',
    fieldKey: 'supplier',
    fieldName: '供应商',
    convert: ({ rowData }) => rowData.supplier?.name
  },
  // {
  //   componentType: 'el-input',
  //   componentName: '输入框',
  //   fieldKey: 'supplier-address',
  //   fieldName: '供应商地址',
  //   convert: ({ rowData }) => rowData.supplier?.address
  // },
  // {
  //   componentType: 'el-input',
  //   componentName: '输入框',
  //   fieldKey: 'accountAlias',
  //   fieldName: '供应商账户',
  //   convert: ({ rowData }) => rowData.account?.alias
  // },
  {
    componentType: 'el-input',
    componentName: '输入框',
    fieldKey: 'containsRate',
    fieldName: '是否含税',
    formatType: 'boolean'
  },
  {
    componentType: 'el-input',
    componentName: '输入框',
    fieldKey: 'rate',
    fieldName: '税率',
    formatType: 'percent',
    textAlign: 'right'
  },
  {
    componentType: 'el-input',
    componentName: '输入框',
    fieldKey: 'fee',
    fieldName: '单价(含税)',
    isMoney: true,
    textAlign: 'right'
  },
  {
    componentType: 'el-input',
    componentName: '输入框',
    fieldKey: 'feeWithoutRate',
    fieldName: '单价(未税)',
    isMoney: true,
    textAlign: 'right',
    convert: context => {
      const { rowData } = context
      const { fee } = rowData
      if (!fee) {
        return ''
      }
      const { rate } = rowData
      return priceWithoutRate(fee, true, rate)
    }
  },
  {
    componentType: 'el-input',
    componentName: '输入框',
    fieldKey: 'totalPrice',
    fieldName: '总价(未税)',
    isMoney: true,
    textAlign: 'right'
  },
  {
    componentType: 'el-input',
    componentName: '输入框',
    fieldKey: 'totalPriceWithRate',
    fieldName: '总价',
    isMoney: true,
    textAlign: 'right'
  },
  {
    componentType: 'el-input',
    componentName: '输入框',
    fieldKey: 'amount',
    fieldName: '采购数量',
    textAlign: 'right'
  },
  {
    componentType: 'el-input',
    componentName: '输入框',
    fieldKey: 'storageNum',
    fieldName: '已入库数量',
    textAlign: 'right'
  },
  {
    componentType: 'el-input',
    componentName: '输入框',
    fieldKey: 'virtualStorage',
    fieldName: '虚拟入库',
    convert: ({ rowData }) => rowData.virtualStorage ? '是' : '否'
  },
  {
    componentType: 'el-input',
    componentName: '输入框',
    fieldKey: 'createTime',
    fieldName: '创建时间'
  },
  {
    componentType: 'el-input',
    componentName: '输入框',
    fieldKey: 'approveUser',
    fieldName: '审批人'
  },
  // {
  //   componentType: 'el-input',
  //   componentName: '输入框',
  //   fieldKey: 'approveTime',
  //   fieldName: '审批时间'
  // },
  // {
  //   componentType: 'el-input',
  //   componentName: '输入框',
  //   fieldKey: 'payments-account',
  //   fieldName: '付款账户',
  //   convert: ({ rowData }) => rowData.payments?.map(item => item.account).join('，')
  // },
  // {
  //   componentType: 'el-input',
  //   componentName: '输入框',
  //   fieldKey: 'payments-amount',
  //   fieldName: '付款金额',
  //   convert: ({ rowData }) => rowData.payments?.map(item => $currency(item.amount)).join('，')
  // },
  // {
  //   componentType: 'el-input',
  //   componentName: '输入框',
  //   fieldKey: 'payments-date',
  //   fieldName: '付款时间',
  //   convert: ({ rowData }) => rowData.payments?.map(item => item.date).join('，')
  // },
  // {
  //   componentType: 'el-input',
  //   componentName: '输入框',
  //   fieldKey: 'payments-createTime',
  //   fieldName: '付款时间',
  //   convert: ({ rowData }) => rowData.payments?.map(item => item.createTime).join('，')
  // },
  // {
  //   componentType: 'el-input',
  //   componentName: '输入框',
  //   fieldKey: 'invoices-invoiceTypeValue',
  //   fieldName: '发票类型',
  //   convert: ({ rowData }) => rowData.invoices?.map(item => item.invoiceTypeValue).join('，')
  // },
  // {
  //   componentType: 'el-input',
  //   componentName: '输入框',
  //   fieldKey: 'invoices-invoiceNo',
  //   fieldName: '发票号码',
  //   convert: ({ rowData }) => rowData.invoices?.map(item => item.invoiceNo)
  //     .map(value => hasText(value) ? value : '无')
  //     .join('，')
  // },
  // {
  //   componentType: 'el-input',
  //   componentName: '输入框',
  //   fieldKey: 'invoices-invoiceCode',
  //   fieldName: '发票代码',
  //   convert: ({ rowData }) => rowData.invoices?.map(item => item.invoiceCode)
  //     .map(value => hasText(value) ? value : '无')
  //     .join('，')
  // },
  // {
  //   componentType: 'el-input',
  //   componentName: '输入框',
  //   fieldKey: 'invoices-invoiceCheckCode',
  //   fieldName: '发票校验码',
  //   convert: ({ rowData }) => rowData.invoices?.map(item => item.invoiceCheckCode)
  //     .map(value => hasText(value) ? value : '无').join('，')
  // },
  // {
  //   componentType: 'el-input',
  //   componentName: '输入框',
  //   fieldKey: 'invoices-invoiceAmount',
  //   fieldName: '发票金额',
  //   convert: ({ rowData }) => rowData.invoices?.map(item => $currency(item.invoiceAmount)).join('，')
  // },
  // {
  //   componentType: 'el-input',
  //   componentName: '输入框',
  //   fieldKey: 'invoices-invoiceDate',
  //   fieldName: '开票时间',
  //   convert: ({ rowData }) => rowData.invoices?.map(item => item.invoiceDate).join('，')
  // },
  // {
  //   componentType: 'el-input',
  //   componentName: '输入框',
  //   fieldKey: 'invoices-createTime',
  //   fieldName: '登记时间',
  //   convert: ({ rowData }) => rowData.invoices?.map(item => item.createTime).join('，')
  // },
  // {
  //   componentType: 'el-input',
  //   componentName: '输入框',
  //   fieldKey: 'invoiceStatusValue',
  //   fieldName: '开票状态'
  // },
  // {
  //   componentType: 'el-input',
  //   componentName: '输入框',
  //   fieldKey: 'paymentStatusValue',
  //   fieldName: '付款状态'
  // },
  {
    componentType: 'el-input',
    componentName: '输入框',
    fieldKey: 'storageStatusValue',
    fieldName: '入库状态'
  },
  // {
  //   componentType: 'el-input',
  //   componentName: '输入框',
  //   fieldKey: 'total',
  //   fieldName: '应付费用',
  //   textAlign: 'right',
  //   convert: ({ rowData }) => $currency(rowData.total)
  // },
  // {
  //   componentType: 'el-input',
  //   componentName: '输入框',
  //   fieldKey: 'paid',
  //   fieldName: '已付费用',
  //   textAlign: 'right',
  //   convert: ({ rowData }) => $currency(rowData.paid)
  // },
  // {
  //   componentType: 'el-input',
  //   componentName: '输入框',
  //   fieldKey: 'invoicedAmount',
  //   fieldName: '已开票费用',
  //   textAlign: 'right'
  // },
  {
    componentType: 'el-input',
    componentName: '输入框',
    fieldKey: 'createUserName',
    fieldName: '创建人'
  },
  {
    componentType: 'el-input',
    componentName: '输入框',
    fieldKey: 'requestUsers',
    fieldName: '申请人',
    convert: ({ rowData }) => rowData.requestUsers?.join('，') ?? '直接采购'
  },
  {
    componentType: 'el-input',
    componentName: '输入框',
    fieldKey: 'mark',
    fieldName: '备注',
    width: 200
  },
  {
    componentType: 'el-input',
    componentName: '输入框',
    fieldKey: 'material-no',
    fieldName: '物料编号',
    convert: ({ rowData }) => rowData.material?.no
  },
  {
    componentType: 'el-input',
    componentName: '输入框',
    fieldKey: 'material-class',
    fieldName: '物料类别',
    convert: ({ rowData }) => rowData.material?.materialClass
  },
  // {
  //   componentType: 'el-input',
  //   componentName: '输入框',
  //   fieldKey: 'material-unit',
  //   fieldName: '物料单位',
  //   convert: ({ rowData }) => rowData.material?.materialUnit
  // },
  {
    componentType: 'el-input',
    componentName: '输入框',
    fieldKey: 'material-name',
    fieldName: '物料名称',
    convert: ({ rowData }) => rowData.material?.name
  },
  {
    componentType: 'el-input',
    componentName: '输入框',
    fieldKey: 'material-brand',
    fieldName: '物料品牌',
    convert: ({ rowData }) => rowData.material?.brand
  },
  {
    componentType: 'el-input',
    componentName: '输入框',
    fieldKey: 'material-model',
    fieldName: '物料型号',
    convert: ({ rowData }) => rowData.material?.model
  }
  // {
  //   componentType: 'el-input',
  //   componentName: '输入框',
  //   fieldKey: 'material-code',
  //   fieldName: '物料代码',
  //   convert: ({ rowData }) => rowData.material?.code
  // }
]
